/**
 * LunkrBytes Hero — New Arrivals slideshow
 *
 * Task 1 scope: slide sizing only.
 *
 * Slide extent is written here in pixels, measured from the track's own box,
 * because the CSS-percentage version of this is circular: the track sizes to
 * its rows and the rows size to the track. The browser resolves that cycle by
 * sizing slides intrinsically first, which inflated this hero to 5014px and
 * rendered every tab as a 1491px vertical sliver.
 *
 * The axis is read from computed grid-auto-flow rather than from a media
 * query, so the CSS breakpoint stays the single source of truth.
 */
(function () {
  'use strict';

  function isHorizontal(track) {
    return window.getComputedStyle(track).gridAutoFlow.indexOf('column') !== -1;
  }

  function sizeSlides(root, track) {
    var cs = window.getComputedStyle(track);
    var horizontal = isHorizontal(track);
    var rect = track.getBoundingClientRect();

    if (horizontal) {
      /* Clear the other axis BEFORE the zero-guard: a transient 0 measurement
         while the axis is flipping must not leave both properties live. */
      track.style.removeProperty('--lb-na-slide-h');

      /* Roughly 1.6 slides in view, so the strip visibly continues past the
         right edge and reads as scrollable. */
      var width = rect.width * 0.62;
      if (width <= 0) return;
      track.style.setProperty('--lb-na-slide-w', width + 'px');
    } else {
      track.style.removeProperty('--lb-na-slide-w');

      var perPage = parseInt(root.dataset.perPage, 10) || 3;
      var gap = parseFloat(cs.rowGap) || 0;
      var height = (rect.height - (perPage - 1) * gap) / perPage;
      if (height <= 0) return;
      track.style.setProperty('--lb-na-slide-h', height + 'px');
    }

    /* Assumes the track is never display:none while mounted. If a future
       change hides the tile at some breakpoint, data-na-sized can stay unset
       because the zero-guards above return before reaching here. */
    track.dataset.naSized = 'true';
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Per-root teardown, so a theme-editor re-render does not leave listeners
     and observers bound to a detached node. The editor injects server-rendered
     HTML with no data-na-init, so the init guard alone cannot prevent this. */
  var teardowns = (typeof WeakMap === 'function') ? new WeakMap() : null;

  /* Offsets are read as DIFFERENCES so the maths holds whatever element ends
     up being the slides' offsetParent. */
  function offsetDelta(from, to, horizontal) {
    return horizontal
      ? to.offsetLeft - from.offsetLeft
      : to.offsetTop - from.offsetTop;
  }

  function init(root) {
    var track = root.querySelector('[data-hero-new-arrivals-track]');
    if (!track) return;
    if (root.dataset.naInit === 'true') return;
    root.dataset.naInit = 'true';

    var disposers = [];

    function registerTeardown() {
      if (!teardowns) return;
      teardowns.set(root, function () {
        for (var i = 0; i < disposers.length; i++) {
          try { disposers[i](); } catch (e) {}
        }
      });
    }

    var scheduled = false;

    function schedule() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        sizeSlides(root, track);
      });
    }

    schedule();

    /* Observe the TILE, not the track. Observing the track would re-fire on
       the size change this callback itself causes. */
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(schedule);
      ro.observe(root.parentNode || root);
      disposers.push(function () { ro.disconnect(); });
    }
    window.addEventListener('resize', schedule);
    disposers.push(function () { window.removeEventListener('resize', schedule); });

    /* Images arriving late can shift the box on the first paint. */
    window.addEventListener('load', schedule);
    disposers.push(function () { window.removeEventListener('load', schedule); });

    var slides = track.children;
    var realCount = parseInt(root.dataset.slideCount, 10) || 0;
    var interval = parseInt(root.dataset.interval, 10) || 4000;
    var perPage = parseInt(root.dataset.perPage, 10) || 3;

    /* No duplicate set was rendered, so everything is already in view and
       there is nothing to loop through. */
    if (slides.length <= realCount || slides.length < 2) {
      registerTeardown();
      return;
    }

    /* Autoplay only where a pointer can hover. On touch there is no hover to
       pause with and tapping a slide navigates away, so an auto-advancing
       strip cannot be stopped (WCAG 2.2.2) and fights the visitor's own
       swipe. The strip stays swipeable; it just does not move on its own. */
    if (!window.matchMedia('(hover: hover)').matches) {
      registerTeardown();
      return;
    }

    var timer = null;
    var hovered = false;
    var focused = false;
    var visible = true;

    function maybeStart() {
      if (!hovered && !focused) start();
    }

    function step() {
      var horizontal = isHorizontal(track);
      var loopLen = offsetDelta(slides[0], slides[realCount], horizontal);
      var pitch = offsetDelta(slides[0], slides[1], horizontal);
      if (loopLen <= 0 || pitch <= 0) return;

      var pos = horizontal ? track.scrollLeft : track.scrollTop;

      /* Rewind across the duplicate BEFORE advancing. The duplicate is
         identical, so landing on the equivalent slide in the first copy is
         invisible — unlike scrolling all the way back to the top. */
      if (pos >= loopLen) {
        if (horizontal) track.scrollLeft = pos - loopLen;
        else track.scrollTop = pos - loopLen;
      }

      var advance = pitch * (horizontal ? 1 : perPage);
      if (horizontal) track.scrollBy({ left: advance, behavior: 'smooth' });
      else track.scrollBy({ top: advance, behavior: 'smooth' });
    }

    function start() {
      if (timer || hovered || focused || !visible || reducedMotion.matches || document.hidden) return;
      timer = window.setInterval(step, interval);
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }
    disposers.push(function () { stop(); });

    root.addEventListener('mouseenter', function () { hovered = true; stop(); });
    root.addEventListener('mouseleave', function () { hovered = false; maybeStart(); });
    root.addEventListener('focusin', function () { focused = true; stop(); });
    root.addEventListener('focusout', function () { focused = false; maybeStart(); });

    var onVisibility = function () {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    disposers.push(function () { document.removeEventListener('visibilitychange', onVisibility); });

    var onReducedMotionChange = function () {
      if (reducedMotion.matches) stop();
      else start();
    };
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', onReducedMotionChange);
      disposers.push(function () {
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', onReducedMotionChange);
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          visible = entry.isIntersecting;
          if (visible) start();
          else stop();
        });
      }, { threshold: 0.2 });
      io.observe(root);
      disposers.push(function () { io.disconnect(); });
    } else {
      start();
    }

    registerTeardown();
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-hero-new-arrivals]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* The theme editor re-renders the whole section on every setting change. */
  document.addEventListener('shopify:section:load', initAll);

  document.addEventListener('shopify:section:unload', function (event) {
    if (!teardowns || !event.target || !event.target.querySelector) return;
    var root = event.target.querySelector('[data-hero-new-arrivals]');
    if (root && teardowns.has(root)) {
      teardowns.get(root)();
      teardowns['delete'](root);
    }
  });
})();
